import React, { useMemo } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Brand, Category, Product } from "../../../types";
import { FaqTarget, FaqTargetInput } from "../../../services/answerDeskService";
import { ProductPicker } from "./ProductPicker";

// One editable "applies to" rule. A products rule fans out to one target
// row per product; the others map 1:1 to a target row (brand AND category
// when both are set).
export type Rule =
    | { key: string; kind: "products"; products: Product[] }
    | { key: string; kind: "category"; categoryId: string | null; includeSub: boolean }
    | { key: string; kind: "brand"; brandId: string | null }
    | { key: string; kind: "brand_category"; brandId: string | null; categoryId: string | null; includeSub: boolean };

let ruleSeq = 0;
const nextKey = () => `rule-${++ruleSeq}`;

export function newRule(kind: Rule["kind"], products: Product[] = []): Rule {
    switch (kind) {
        case "products":
            return { key: nextKey(), kind, products };
        case "category":
            return { key: nextKey(), kind, categoryId: null, includeSub: true };
        case "brand":
            return { key: nextKey(), kind, brandId: null };
        case "brand_category":
            return { key: nextKey(), kind, brandId: null, categoryId: null, includeSub: true };
    }
}

/** Saved targets → editable rules (all product targets share one row). */
export function rulesFromTargets(targets: FaqTarget[]): Rule[] {
    const rules: Rule[] = [];
    const products = targets
        .filter((t) => t.product_id)
        .map((t) => ({ product_id: t.product_id as string, name: t.product_name ?? "Deleted product" }) as Product);
    if (products.length) rules.push({ key: nextKey(), kind: "products", products });
    for (const t of targets) {
        if (t.product_id) continue;
        if (t.brand_id && t.category_id) {
            rules.push({ key: nextKey(), kind: "brand_category", brandId: t.brand_id, categoryId: t.category_id, includeSub: t.include_subcategories });
        } else if (t.category_id) {
            rules.push({ key: nextKey(), kind: "category", categoryId: t.category_id, includeSub: t.include_subcategories });
        } else if (t.brand_id) {
            rules.push({ key: nextKey(), kind: "brand", brandId: t.brand_id });
        }
    }
    return rules;
}

/** Editable rules → API targets; incomplete rows are skipped. */
export function targetsFromRules(rules: Rule[]): FaqTargetInput[] {
    const out: FaqTargetInput[] = [];
    for (const r of rules) {
        if (r.kind === "products") {
            r.products.forEach((p) => out.push({ product_id: p.product_id }));
        } else if (r.kind === "category" && r.categoryId) {
            out.push({ category_id: r.categoryId, include_subcategories: r.includeSub });
        } else if (r.kind === "brand" && r.brandId) {
            out.push({ brand_id: r.brandId });
        } else if (r.kind === "brand_category" && r.brandId && r.categoryId) {
            out.push({ brand_id: r.brandId, category_id: r.categoryId, include_subcategories: r.includeSub });
        }
    }
    return out;
}

export function hasIncompleteRule(rules: Rule[]): boolean {
    return rules.some(
        (r) =>
            (r.kind === "products" && r.products.length === 0) ||
            (r.kind === "category" && !r.categoryId) ||
            (r.kind === "brand" && !r.brandId) ||
            (r.kind === "brand_category" && (!r.brandId || !r.categoryId)),
    );
}

const RULE_LABEL: Record<Rule["kind"], string> = {
    products: "Products",
    category: "Category",
    brand: "Brand",
    brand_category: "Brand + Category",
};

interface TargetsEditorProps {
    rules: Rule[];
    onChange: (rules: Rule[]) => void;
    categories: Category[];
    brands: Brand[];
}

export const TargetsEditor: React.FC<TargetsEditorProps> = ({ rules, onChange, categories, brands }) => {
    // "Cat Food › Pouch" style labels so similarly named subcategories are clear.
    const categoryLabels = useMemo(() => {
        const byId = new Map(categories.map((c) => [c.category_id, c]));
        const label = (c: Category): string => {
            const parts = [c.name];
            let parent = c.parent_id ? byId.get(c.parent_id) : undefined;
            let guard = 0;
            while (parent && guard++ < 10) {
                parts.unshift(parent.name);
                parent = parent.parent_id ? byId.get(parent.parent_id) : undefined;
            }
            return parts.join(" › ");
        };
        return new Map(categories.map((c) => [c.category_id, label(c)]));
    }, [categories]);

    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => (categoryLabels.get(a.category_id) ?? "").localeCompare(categoryLabels.get(b.category_id) ?? "")),
        [categories, categoryLabels],
    );
    const sortedBrands = useMemo(() => [...brands].sort((a, b) => a.name.localeCompare(b.name)), [brands]);

    const update = (key: string, patch: Partial<Rule>) =>
        onChange(rules.map((r) => (r.key === key ? ({ ...r, ...patch } as Rule) : r)));
    const remove = (key: string) => onChange(rules.filter((r) => r.key !== key));

    const categoryField = (rule: Extract<Rule, { categoryId: string | null }>) => (
        <Autocomplete
            size="small"
            options={sortedCategories}
            value={sortedCategories.find((c) => c.category_id === rule.categoryId) ?? null}
            onChange={(_, c) => update(rule.key, { categoryId: c?.category_id ?? null })}
            getOptionLabel={(c) => categoryLabels.get(c.category_id) ?? c.name}
            isOptionEqualToValue={(a, b) => a.category_id === b.category_id}
            renderInput={(params) => <TextField {...params} label="Category" />}
            sx={{ flex: "1 1 220px", minWidth: 0 }}
        />
    );
    const brandField = (rule: Extract<Rule, { brandId: string | null }>) => (
        <Autocomplete
            size="small"
            options={sortedBrands}
            value={sortedBrands.find((b) => b.brand_id === rule.brandId) ?? null}
            onChange={(_, b) => update(rule.key, { brandId: b?.brand_id ?? null })}
            getOptionLabel={(b) => b.name}
            isOptionEqualToValue={(a, b) => a.brand_id === b.brand_id}
            renderInput={(params) => <TextField {...params} label="Brand" />}
            sx={{ flex: "1 1 180px", minWidth: 0 }}
        />
    );
    const subcategoriesBox = (rule: Extract<Rule, { includeSub: boolean }>) => (
        <FormControlLabel
            control={
                <Checkbox
                    size="small"
                    checked={rule.includeSub}
                    onChange={(e) => update(rule.key, { includeSub: e.target.checked })}
                />
            }
            label={<Typography variant="body2">include subcategories</Typography>}
            sx={{ mr: 0 }}
        />
    );

    return (
        <Stack spacing={1}>
            {rules.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Add at least one rule, or mark the answer General.
                </Typography>
            )}
            {rules.map((rule) => (
                <Paper key={rule.key} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary", width: { xs: "100%", sm: 120 }, textTransform: "uppercase" }}
                        >
                            {RULE_LABEL[rule.kind]}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            {rule.kind === "products" && (
                                <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                                    <ProductPicker
                                        size="small"
                                        label="Products"
                                        value={rule.products}
                                        onChange={(products) => update(rule.key, { products })}
                                        max={50}
                                    />
                                </Box>
                            )}
                            {rule.kind === "category" && (
                                <>
                                    {categoryField(rule)}
                                    {subcategoriesBox(rule)}
                                </>
                            )}
                            {rule.kind === "brand" && brandField(rule)}
                            {rule.kind === "brand_category" && (
                                <>
                                    {brandField(rule)}
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                        AND
                                    </Typography>
                                    {categoryField(rule)}
                                    {subcategoriesBox(rule)}
                                </>
                            )}
                        </Box>
                        <Tooltip title="Remove this rule">
                            <IconButton size="small" onClick={() => remove(rule.key)} aria-label="Remove this rule">
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Paper>
            ))}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                    Add rule:
                </Typography>
                {(["products", "category", "brand", "brand_category"] as Rule["kind"][]).map((kind) => (
                    <Button
                        key={kind}
                        size="small"
                        variant="outlined"
                        onClick={() => onChange([...rules, newRule(kind)])}
                        disabled={kind === "products" && rules.some((r) => r.kind === "products")}
                    >
                        {RULE_LABEL[kind]}
                    </Button>
                ))}
            </Stack>
        </Stack>
    );
};
